import { useEffect, useState } from 'react';

import { FaTrash } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { colors } from 'constants/colors';
import { PAGES } from '../../../constants/pages';

import { DetailLink } from 'components/DetailsView/DetailLink';
import { SectionColumn } from 'components/DetailsView/Section';
import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { ActionsAndSave } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';
import { TotemInput } from 'components/TotemInput';
import { TotemSelect } from 'components/TotemSelect';

import {
    ProductGroupFragmentFragment,
    useGetAllProductsQuery,
    useGetProductGroupQuery,
    useUpdateProductGroupMutation,
    TotadminPermission,
} from 'data/__generated__';

type ParamTypes = {
    productGroupId: string;
};

export const ProductGroupDetails = () => {
    const { productGroupId = '' } = useParams<ParamTypes>();

    const [productGroupComposition, setProductGroupComposition] = useState<
        Omit<ProductGroupFragmentFragment['composition'][number], '__typename'>[]
    >([]);

    const [updateProductGroup] = useUpdateProductGroupMutation();
    const {
        loading: productGroupLoading,
        data: productGroupData,
        error: productGroupError,
    } = useGetProductGroupQuery({ variables: { productGroupId } });

    const { loading: productsLoading, data: productsData, error: productsError } = useGetAllProductsQuery();

    useEffect(() => {
        if (productGroupData?.getProductGroup.composition) {
            setProductGroupComposition(
                productGroupData.getProductGroup.composition.map(({ productId, quantity }) => ({
                    productId,
                    quantity,
                })),
            );
        }
    }, [productGroupData, setProductGroupComposition]);

    if (productsLoading || productGroupLoading) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (productsError || !productsData || productGroupError || !productGroupData) {
        throw new Error('Une erreur est survenue lors de la récupération des produits');
    }

    const { getAllProducts: products } = productsData;
    const { getProductGroup: productGroup } = productGroupData;

    const handleProductSelect = (productId: string | null) => {
        if (productId) {
            const product = products.find(({ _id }) => _id === productId);
            if (product) {
                if (!productGroupComposition.some(({ productId: id }) => product._id === id)) {
                    setProductGroupComposition([...productGroupComposition, { productId: product._id, quantity: 1 }]);
                }
            }
        }
    };

    const handleQuantityUpdate = ({ productId, value }: { productId: string; value: string }) => {
        // Check that the string only contains digits
        if (/^(\d+)?$/.test(value)) {
            setProductGroupComposition(
                productGroupComposition.map((compositionEntry) => {
                    if (compositionEntry.productId !== productId) {
                        return compositionEntry;
                    }
                    return {
                        productId,
                        quantity: Number(value),
                    };
                }),
            );
        }
    };

    const handleProductDelete = (productId: string) => {
        setProductGroupComposition(
            productGroupComposition.filter((compositionEntry) => compositionEntry.productId !== productId),
        );
    };

    const handleSave = async () => {
        const { data } = await updateProductGroup({
            variables: {
                productGroupId: productGroup._id,
                composition: productGroupComposition,
            },
        });
        if (!data) {
            toast.error('Une erreur inconnue est survenue lors de la modification du regroupement de produits');
            return;
        }

        toast.success('Le regroupement de produits a bien été modifié');
    };

    const productGroupProduct = products.find(({ _id }) => _id === productGroup.productId);

    const productsOptions = products.map((product) => ({
        value: product._id,
        label: product.fullname,
    }));

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.productGroupDetails} />
                </HeaderTitle>
                <ActionsAndSave
                    actionGroups={[]}
                    onSave={handleSave}
                    saveTitle="Mettre à jour"
                    saveRequiredPermissions={[TotadminPermission.ProductGroupsManage]}
                />
            </Header>
            <Content>
                <ScrollableContent>
                    <SectionColumn>
                        <DetailLink
                            label={productGroupProduct?.fullname ?? 'Produit inexistant'}
                            path="product"
                            value={productGroup.productId}
                        />

                        <TotemSelect
                            label="Ajouter un produit"
                            placeholder="Sélectionner un produit (recherche par nom ou par _id)"
                            value={null}
                            options={productsOptions}
                            onChange={(value) => handleProductSelect(value)}
                        />
                        <ProductsTable>
                            <thead>
                                <tr>
                                    <ProductsTableHeaderCell>Produit</ProductsTableHeaderCell>
                                    <ProductsTableHeaderCell $isDataCentered>Quantité</ProductsTableHeaderCell>

                                    <ProductsTableHeaderCell $isDataCentered>Supprimer</ProductsTableHeaderCell>
                                </tr>
                            </thead>
                            <ProductsTableBody>
                                {productGroupComposition.map(({ productId, quantity }) => {
                                    const product = products.find(({ _id }) => _id === productId);

                                    return (
                                        <tr key={productId}>
                                            <ProductsTableBodyCell>
                                                <ProductNameContainer>
                                                    <DetailLink
                                                        name={product?.fullname ?? 'No product found, weird'}
                                                        value={productId}
                                                        path="product"
                                                    />
                                                </ProductNameContainer>
                                            </ProductsTableBodyCell>
                                            <ProductsTableBodyCell $isDataCentered>
                                                <QuantityInputOuterContainer>
                                                    <QuantityInputInnerContainer>
                                                        <TotemInput
                                                            type="number"
                                                            value={quantity || ''}
                                                            onChange={(value) =>
                                                                handleQuantityUpdate({ productId, value })
                                                            }
                                                            $centerText
                                                            data-test="product-quantity"
                                                        />
                                                    </QuantityInputInnerContainer>
                                                </QuantityInputOuterContainer>
                                            </ProductsTableBodyCell>

                                            <ProductsTableBodyCell $isDataCentered>
                                                <DeleteIconContainer onClick={() => handleProductDelete(productId)}>
                                                    <FaTrash data-test="trash-icon" size={20} color={colors.red} />
                                                </DeleteIconContainer>
                                            </ProductsTableBodyCell>
                                        </tr>
                                    );
                                })}
                            </ProductsTableBody>
                        </ProductsTable>
                    </SectionColumn>
                </ScrollableContent>
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;
`;

const ProductsTable = styled.table`
    border-radius: ${({ theme }) => theme.borderRadius};
    border-spacing: 0px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
`;

const ProductsTableBody = styled.thead`
    & > :not(:first-child) td {
        border-top: 1px solid ${({ theme }) => theme.lightBorderColor};
    }
`;

const ProductsTableHeaderCell = styled.th<{ $isDataCentered?: boolean }>`
    padding: 10px;
    text-align: ${({ $isDataCentered }) => ($isDataCentered ? 'center' : 'left')};
    border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
`;

const ProductsTableBodyCell = styled.td<{ $isDataCentered?: boolean }>`
    padding: 10px;
    text-align: ${({ $isDataCentered }) => ($isDataCentered ? 'center' : 'left')};
`;

const ProductNameContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const QuantityInputOuterContainer = styled.div`
    display: flex;
    justify-content: center;
`;

const QuantityInputInnerContainer = styled.div`
    width: 70px;
`;

const DeleteIconContainer = styled.div`
    display: inline;
    padding: 5px;
    cursor: pointer;

    &:hover {
        opacity: 0.8;
    }
`;
